import Component from '@glimmer/component';
import { service } from '@ember/service';
import { concat } from '@ember/helper';
import { hash } from '@ember/helper';
import { filterBy } from '@nullvoxpopuli/ember-composable-helpers';
import { t } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';
import { variation } from 'ember-launch-darkly';
import {
  HdsBadge,
  HdsDropdown,
  HdsLinkInline,
  HdsPageHeader,
  HdsTabs,
} from '@hashicorp/design-system-components/components';
import {
  BreadcrumbsContainer,
  CoreCardSummary,
  CoreLayoutMain,
  CoreProjectLinks,
} from 'core/components';
import { optionForSourceState } from 'core/helpers';
import { boxMargin } from 'core/modifiers';
import { checkReleaseAvailability } from 'core/utils/release';
import HcpAlertTerraformUnification from 'hcp/components/hcp/alert/terraform-unification';
import LocationLink from 'hcp/components/location-link';
import ResourceCard from 'hcp/components/page/projects/dashboard/resource-card';
import PageProjectsDashboardBillingSummaryCard from 'hcp/components/page/projects/dashboard/billing-summary-card';
import PageProjectsDashboardServiceCard from 'hcp/components/page/projects/dashboard/service-card';
import TerraformCard from 'hcp/components/page/projects/dashboard/terraform-card';

/**
 *
 * `PageProjectsDashboard` displays a dashboard for a Project.
 *
 *
 * ```
 * <Page::Projects::Dashboard
 *   @organization={{this.model.organization}}
 *   @project={{this.model.project}}
 *   @resources={{this.model.resources}}
 * />
 * ```
 *
 * @class PageProjectsDashboard
 *
 */
/**
 *
 * `organization` is the organization context.
 * @argument organization;
 * @type {Object}
 *
 */
/**
 *
 * `project` is the project context.
 * @argument project;
 * @type {Object}
 *
 */
/**
 *
 * `resources` is a collection of active resources
 * @argument resources;
 * @type {Object}
 *
 */

export default class PageProjectsDashboard extends Component {
  @service abilities;
  @service('resources.project') projects;
  @service userContext;
  @service config;

  get cards() {
    const productCards = [];

    productCards.push({
      component: {
        args: {
          product: null,
        },
        name: ResourceCard,
      },
      isAvailable: this.abilities.can('list resources'),
      isReleased: true,
    });

    productCards.push({
      component: {
        args: {
          product: null,
        },
        name: PageProjectsDashboardBillingSummaryCard,
      },
      isAvailable: this.abilities.can('view billing-account'),
      isReleased: true,
    });

    productCards.push({
      component: {
        args: {
          product: 'boundary',
        },
        name: PageProjectsDashboardServiceCard,
      },
      isAvailable: this.abilities.can('list boundary-clusters'),
      isReleased: checkReleaseAvailability(
        'boundary',
        this.config.app?.deployGeo
      ),
    });

    productCards.push({
      component: {
        args: {
          product: 'consul',
        },
        name: PageProjectsDashboardServiceCard,
      },
      isAvailable: this.abilities.can('list consul'),
      isReleased: checkReleaseAvailability(
        'consul',
        this.config.app?.deployGeo
      ),
    });

    productCards.push({
      component: {
        args: {
          product: 'packer',
        },
        name: PageProjectsDashboardServiceCard,
      },
      isAvailable: this.abilities.can('get packer-registry'),
      isReleased: checkReleaseAvailability(
        'packer',
        this.config.app?.deployGeo
      ),
    });

    productCards.push({
      component: {
        args: {},
        name: TerraformCard,
      },
      isAvailable: variation('hcp-ui-terraform-dashboard-tile'),
      isReleased: checkReleaseAvailability(
        'terraform',
        this.config.app?.deployGeo
      ),
    });

    productCards.push({
      component: {
        args: {
          product: 'vagrant',
        },
        name: PageProjectsDashboardServiceCard,
      },
      isAvailable: this.abilities.can('list vagrant-registries'),
      isReleased: checkReleaseAvailability(
        'vagrant',
        this.config.app?.deployGeo
      ),
    });

    productCards.push({
      component: {
        args: {
          product: 'vault',
        },
        name: PageProjectsDashboardServiceCard,
      },
      isAvailable: this.abilities.can('list vault'),
      isReleased: checkReleaseAvailability('vault', this.config.app?.deployGeo),
    });

    productCards.push({
      component: {
        args: {
          product: 'radar',
        },
        name: PageProjectsDashboardServiceCard,
      },
      isAvailable: this.abilities.can('view vault-radar'),
      isReleased: checkReleaseAvailability(
        'vault-radar',
        this.config.app?.deployGeo
      ),
    });

    // TODO: HVT-7537 After HVS reaches EOL, remove secretsApps + hvs product card
    productCards.push({
      component: {
        args: {
          product: 'secrets',
        },
        name: PageProjectsDashboardServiceCard,
      },
      isAvailable: this.abilities.can('list vault-secrets-app', {
        canAccessHvs: this?.userContext?.organizationMeta?.canAccessHvs,
        secretsApps: this.args?.secretsApps,
        billing: this?.args?.billing,
      }),
      isReleased: checkReleaseAvailability(
        'vault-secrets',
        this.config.app?.deployGeo
      ),
    });

    productCards.push({
      component: {
        args: {
          product: 'waypoint',
        },
        name: PageProjectsDashboardServiceCard,
      },
      isAvailable: this.abilities.can('read waypoint-application'),
      isReleased: checkReleaseAvailability(
        'waypoint',
        this.config.app?.deployGeo
      ),
    });

    return productCards;
  }

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          {{#if (variation "hcp-ui-left-side-nav-new-structure")}}
            <PH.Title data-test-project-dashboard-title>
              {{t "nav.name-dashboard" name=(t "nav.project.header")}}
            </PH.Title>
          {{else}}
            <PH.Title data-test-project-dashboard-title>
              {{@project.name}}
            </PH.Title>
          {{/if}}
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.IconTile @icon="dashboard" />
          {{#if (not (variation "hcp-ui-left-side-nav-new-structure"))}}
            <PH.Subtitle data-test-project-dashboard-subtitle>
              {{t "nav.name-dashboard" name=(t "nav.project.header")}}
            </PH.Subtitle>
          {{/if}}
          <PH.Description>
            <span data-test-project-dashboard-description>
              {{t "components.page.projects.dashboard.overview-of-a-project"}}
            </span>
            <HdsLinkInline
              class="hds-typography-body-200"
              @color="secondary"
              @href="https://developer.hashicorp.com/hcp/docs/hcp/admin/projects"
              @icon="external-link"
              data-test-project-dashboard-learn-more
            >
              {{t "components.page.projects.dashboard.learn-more"}}
            </HdsLinkInline>
          </PH.Description>
          <PH.Actions>
            <HdsDropdown
              @listPosition="bottom-right"
              data-test-project-dashboard-manage-dropdown
              as |dd|
            >
              <dd.ToggleButton
                @color="primary"
                @text={{t "components.page.projects.dashboard.manage-project"}}
                data-test-project-dashboard-manage-toggle
              />
              <CoreProjectLinks @component={{dd.Interactive}} />
            </HdsDropdown>
          </PH.Actions>
        </HdsPageHeader>
      </:header>
      <:alert>
        <CoreCardSummary
          @type="horizontal"
          data-test-projects-overview-summary-card
          as |Card|
        >
          <Card.Pair as |P|>
            <P.Key
              data-test-projects-overview-summary-card-key
              data-test-projects-overview-summary-card-key-status
            >{{t "projects-dashboard.project-status"}}</P.Key>
            <P.Value
              data-test-projects-overview-summary-card-value
              data-test-projects-overview-summary-card-value-status
            >
              {{#let
                (optionForSourceState "PROJECT" @project.state)
                as |option|
              }}
                <HdsBadge
                  @color={{option.color}}
                  @icon="check"
                  @isIconOnly={{option.isIconOnly}}
                  @size="small"
                  @text={{t option.text}}
                  @type={{option.type}}
                />
              {{/let}}
            </P.Value>
          </Card.Pair>
          <Card.Pair as |P|>
            <P.Key
              data-test-projects-overview-summary-card-key
              data-test-projects-overview-summary-card-key-last-resource-seen
            >{{t "projects-dashboard.last-resource-seen"}}</P.Key>
            <P.Value
              data-test-projects-overview-summary-card-value
              data-test-projects-overview-summary-card-value-last-resource-seen
            >
              {{#if this.userContext.resource}}
                <LocationLink
                  @link={{this.userContext.resource}}
                  @hideIcon={{true}}
                />
              {{else}}
                —
              {{/if}}
            </P.Value>
          </Card.Pair>
          <Card.LinkStandalone
            @icon="arrow-right"
            @iconPosition="trailing"
            @route="cloud.orgs.detail.projects.detail.settings"
            @text={{t "projects-dashboard.view-project-settings"}}
            data-test-projects-overview-summary-card-standalone-view-project-settings
          />
        </CoreCardSummary>
        {{#if this.userContext.organizationMeta.unifiedExperienceEnabled}}
          <HcpAlertTerraformUnification
            @icon="terraform-square-color"
            @id={{concat
              "terraform-unification-project-dashboard-"
              @organization.id
            }}
            as |A|
          >
            <A.Title>
              {{t
                "components.page.projects.dashboard.terraform-unification-alert.introducing-hcp-terraform-integrated"
              }}
            </A.Title>
            <A.Description>
              {{t
                "components.page.projects.dashboard.terraform-unification-alert.you-can-now-manage"
              }}
            </A.Description>
            <A.LinkStandalone
              @icon="arrow-right"
              @iconPosition="trailing"
              @query={{hash project_id=@project.id}}
              @route="terraform"
              @size="medium"
              @text={{t
                "components.page.projects.dashboard.terraform-unification-alert.get-started-with-hcp-terraform"
              }}
            />
          </HcpAlertTerraformUnification>
        {{/if}}
      </:alert>
      <:body>
        {{#let this.cards as |products|}}
          {{#let
            (filterBy "isReleased" true this.cards)
            (filterBy "isReleased" false this.cards)
            as |releasedProducts unreleasedProducts|
          }}
            {{#if
              (and
                (variation "hcp-ui-project-dashboard-products-discovery-in-geo")
                unreleasedProducts.length
              )
            }}
              <HdsTabs data-test-availability-tabs as |T|>
                <T.Tab @count={{releasedProducts.length}}>
                  {{t "components.page.projects.dashboard.available-services"}}
                </T.Tab>
                <T.Tab @count={{unreleasedProducts.length}}>
                  {{t
                    "components.page.projects.dashboard.unavailable-in-europe"
                  }}
                </T.Tab>
                <T.Panel {{boxMargin "lg 0 0 0"}}>
                  <div class="projectsDashboard__servicesRow">
                    {{#each releasedProducts as |product|}}
                      <product.component.name
                        @billing={{this.userContext.billing}}
                        @organization={{@organization}}
                        @product={{product.component.args.product}}
                        @project={{@project}}
                        @resources={{@resources}}
                        @resourceCount={{or @resources.length 0}}
                      />
                    {{/each}}
                  </div>
                </T.Panel>
                <T.Panel {{boxMargin "lg 0 0 0"}}>
                  <div class="projectsDashboard__servicesRow">
                    {{#each unreleasedProducts as |product|}}
                      <product.component.name
                        @billing={{this.userContext.billing}}
                        @organization={{@organization}}
                        @product={{product.component.args.product}}
                        @project={{@project}}
                        @resources={{@resources}}
                        @resourceCount={{or @resources.length 0}}
                      />
                    {{/each}}
                  </div>
                </T.Panel>
              </HdsTabs>
            {{else}}
              {{#let
                (filterBy
                  "isReleased" true (filterBy "isAvailable" true products)
                )
                as |availableProducts|
              }}
                <div class="projectsDashboard__servicesRow">
                  {{#each availableProducts as |product|}}
                    {{! This applies a collective set of arguments to every card even if
                they do not need them. This is because we cannot spread only the
                necessary arguments to the component helper. }}
                    <product.component.name
                      @billing={{this.userContext.billing}}
                      @organization={{@organization}}
                      @product={{product.component.args.product}}
                      @project={{@project}}
                      @resources={{@resources}}
                      @resourceCount={{or @resources.length 0}}
                    />
                  {{/each}}
                </div>
              {{/let}}
            {{/if}}
          {{/let}}
        {{/let}}
      </:body>
    </CoreLayoutMain>
  </template>
}
